import {
  Actions,
  Button,
  closeCurrentDialog,
  DropdownActionMenuItem,
  FormSubmitHandler,
  openConfirmDialog,
  openDialog,
} from 'platform/components';
import {Hide, HStack} from 'platform/foundation';
import styled from 'styled-components';

import {isNotNil} from 'ramda';

import {useSourcingDeletePresetMutation, useSourcingUpdatePresetMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {buildArray} from 'shared/web';

import {FilterPresetForm, SourcingFilterPresetFormData} from '../FilterPresetForm/FilterPresetForm';

export interface FilterPresetButtonProps {
  id: string;
  title: string;
  count: number | null;
  isSelected?: boolean;
  isSimple?: boolean;
  isNotificationsMuted: boolean | null;
  onClick?: () => void;
  onResetFilterPreset?: () => void;
}

export function FilterPresetButton(props: FilterPresetButtonProps) {
  const [updatePreset] = useSourcingUpdatePresetMutation();
  const [deletePreset] = useSourcingDeletePresetMutation();

  const handleRename = () => {
    const onSubmit: FormSubmitHandler<SourcingFilterPresetFormData> = async (values) =>
      await updatePreset({
        id: props.id,
        updatePresetRequestBody: {
          name: values.name,
          allowNotifications: values.allowNotifications ?? undefined,
        },
      })
        .unwrap()
        .then(closeCurrentDialog)
        .catch(handleApiError);

    openDialog(
      <FilterPresetForm
        initialValues={{
          name: props.title,
          allowNotifications: isNotNil(props.isNotificationsMuted)
            ? !props.isNotificationsMuted
            : null,
        }}
        onSubmit={onSubmit}
      />,
      {
        title: i18n.t('entity.sourcing.filterPresets.labels.rename'),
      }
    );
  };

  const handleEnableNotifications = () => {
    updatePreset({
      id: props.id,
      updatePresetRequestBody: {
        allowNotifications: true,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDisableNotifications = () => {
    updatePreset({
      id: props.id,
      updatePresetRequestBody: {
        allowNotifications: false,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDelete = () => {
    openConfirmDialog({
      text: i18n.t('entity.sourcing.filterPresets.actions.confirmDelete'),
      onConfirm: () => {
        deletePreset({id: props.id}).unwrap().then(props.onResetFilterPreset).catch(handleApiError);
      },
    });
  };

  return (
    <HStack align="center">
      <ButtonGroup>
        <Button
          title={props.title}
          badge={
            isNotNil(props.count) && props.count > 0
              ? {value: `+${props.count}`, colorScheme: 'green'}
              : undefined
          }
          rightIcon={props.isNotificationsMuted ? 'social/notifications_off' : undefined}
          onClick={props.onClick}
          variant={props.isSelected ? 'primary' : 'outlined'}
          data-testid={testIds.sourcing.classifieds(`filterPresetButtons-${props.title}`)}
        />
        <Hide when={props.isSimple}>
          <Actions
            actions={[
              {
                type: 'dropdown-iconButton',
                priority: props.isSelected ? 'primary' : 'tertiary',
                icon: 'navigation/more_vert',
                menuItems: buildArray<DropdownActionMenuItem>([
                  {
                    label: i18n.t('entity.sourcing.filterPresets.actions.rename'),
                    leftIcon: 'image/edit',
                    onClick: handleRename,
                  },
                ])
                  .when(isNotNil(props.isNotificationsMuted) && props.isNotificationsMuted, {
                    label: i18n.t('entity.sourcing.filterPresets.actions.enableNotifications'),
                    leftIcon: 'social/notifications',
                    onClick: handleEnableNotifications,
                  })
                  .when(isNotNil(props.isNotificationsMuted) && !props.isNotificationsMuted, {
                    label: i18n.t('entity.sourcing.filterPresets.actions.disableNotifications'),
                    leftIcon: 'social/notifications_off',
                    onClick: handleDisableNotifications,
                  })
                  .add({
                    label: i18n.t('entity.sourcing.filterPresets.actions.delete'),
                    leftIcon: 'action/delete',
                    severity: 'danger',
                    onClick: handleDelete,
                  }),
              },
            ]}
            data-testid={testIds.sourcing.classifieds('filterPresetButtons-more')}
          />
        </Hide>
      </ButtonGroup>
    </HStack>
  );
}

// Hack for non-standard component
// eslint-disable-next-line eag/no-css-property
const ButtonGroup = styled.div`
  display: flex;

  /* First button (direct child): zero radius on right side, only when multiple children */
  > button:first-child:not(:only-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  /* Middle buttons: all radiuses 0 */
  > button:not(:first-child):not(:last-child) {
    border-radius: 0;
    border-right: 0;
  }

  /* Last button (nested in last child): zero radius on left side */
  > *:last-child button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;
